import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MarketInfoService } from './services/MarketInfoService.service';
import { UnloadCleanupService } from './services/unload-cleanup.service';
import { UserService } from './services/User.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Web Rental';

  private subscriptions: Subscription[] = [];
  private observer: MutationObserver | null = null;
  private currentBoothTerm = 'Booth';
  private isApplyingTerm = false;

  constructor(
    private unloadService: UnloadCleanupService,
    private router: Router,
    private marketInfoService: MarketInfoService,
    private userService: UserService
  ){}

  ngOnInit(): void {
    this.applyConfiguredBoothTerm(this.router.url);

    const routeSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.applyConfiguredBoothTerm(event.urlAfterRedirects || event.url);
      }
    });
    this.subscriptions.push(routeSub);

    this.observer = new MutationObserver(() => {
      if (this.currentBoothTerm !== 'Booth' && !this.isApplyingTerm) {
        this.replaceBoothTermInDocument(this.currentBoothTerm, ['Booth']);
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private applyConfiguredBoothTerm(url: string): void {
    const marketId = this.getMarketIdFromUrl(url) || this.userService.getMarketId();
    if (!marketId) {
      return;
    }

    const settingsSub = this.marketInfoService.getWebRentalSetting(marketId).subscribe((res: any) => {
      const configuredTerm = this.getConfiguredBoothTerm(res);
      if (!configuredTerm) {
        return;
      }

      const sourceTerms = this.currentBoothTerm === configuredTerm
        ? ['Booth']
        : ['Booth', this.currentBoothTerm];

      this.currentBoothTerm = configuredTerm;

      if (configuredTerm !== 'Booth') {
        this.replaceBoothTermInDocument(configuredTerm, sourceTerms);
      }
    });

    this.subscriptions.push(settingsSub);
  }

  private getConfiguredBoothTerm(setting: any): string {
    const customTerm = (setting?.WebRentalBoothTermText || '').trim();
    if (customTerm) {
      return customTerm;
    }

    if (setting?.WebRentalBoothTerm === 0) {
      return 'Space';
    }

    return 'Booth';
  }

  private getMarketIdFromUrl(url: string): string | null {
    if (!url) {
      return null;
    }

    const segments = url.split('?')[0].split('/').filter(Boolean);
    const marketIdSegment = segments.find(segment => /^\d+$/.test(segment));

    return marketIdSegment ?? null;
  }

  private replaceBoothTermInDocument(targetTerm: string, sourceTerms: string[]): void {
    if (!targetTerm) {
      return;
    }

    const effectiveSources = Array.from(new Set(sourceTerms.filter(term => !!term && term !== targetTerm)));
    if (effectiveSources.length === 0) {
      return;
    }

    this.isApplyingTerm = true;

    const textWalker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let textNode = textWalker.nextNode();

    while (textNode) {
      const node = textNode as Text;
      const parentTag = node.parentElement?.tagName;
      if (parentTag === 'SCRIPT' || parentTag === 'STYLE') {
        textNode = textWalker.nextNode();
        continue;
      }

      let updatedText = node.nodeValue || '';

      effectiveSources.forEach(sourceTerm => {
        updatedText = this.replaceTerm(updatedText, sourceTerm, targetTerm);
      });

      if (updatedText !== node.nodeValue) {
        node.nodeValue = updatedText;
      }

      textNode = textWalker.nextNode();
    }

    this.isApplyingTerm = false;
  }

  private replaceTerm(text: string, sourceTerm: string, targetTerm: string): string {
    const pluralSource = sourceTerm.endsWith('s') ? sourceTerm : `${sourceTerm}s`;
    const pluralTarget = targetTerm.endsWith('s') ? targetTerm : `${targetTerm}s`;

    let updated = this.replaceWithCase(text, pluralSource, pluralTarget);
    updated = this.replaceWithCase(updated, sourceTerm, targetTerm);

    return updated;
  }

  private replaceWithCase(text: string, source: string, target: string): string {
    const escapedSource = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escapedSource}\\b`, 'gi');

    return text.replace(regex, (match: string) => {
      if (match === match.toUpperCase()) {
        return target.toUpperCase();
      }

      if (match === match.toLowerCase()) {
        return target.toLowerCase();
      }

      if (match[0] === match[0].toUpperCase() && match.slice(1) === match.slice(1).toLowerCase()) {
        return target.charAt(0).toUpperCase() + target.slice(1).toLowerCase();
      }

      return target;
    });
  }
}
